
import * as React from "react"
import { format } from "date-fns"
import { es } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  date: Date | undefined
  onDateChange: (date: Date | undefined) => void
  className?: string
  showTimeSelect?: boolean
  showAcceptButton?: boolean
  placeholder?: string
  allowPastDates?: boolean
}

export function DatePicker({
  date,
  onDateChange,
  className,
  showTimeSelect = false,
  showAcceptButton = false,
  placeholder = "Seleccionar Fecha",
  allowPastDates = false
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  
  const handleDateSelect = (newDate: Date | undefined) => {
    if (newDate) {
      // Set time to 9:00 AM for all selected dates
      newDate.setHours(9, 0, 0, 0);
    }
    
    onDateChange(newDate);
    
    // Close the popover after selection for visual feedback
    if (!showAcceptButton) {
      setOpen(false);
    }
  };

  // Stop propagation to prevent dialog close
  const handleOpenChange = (open: boolean) => {
    setOpen(open);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          onClick={(e) => e.stopPropagation()}
          type="button"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP", { locale: es }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0 bg-white" 
        align="start"
        style={{ zIndex: 2600 }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          initialFocus
          locale={es}
          className="p-3 pointer-events-auto"
          disabled={allowPastDates ? undefined : { before: new Date() }}
        />
      </PopoverContent>
    </Popover>
  )
}
